import { OLButton } from "OLButton";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasAnyAccessToResource, hasModifyAccessToResource } from "api/general";
import { useMember } from "api/members";
import { useTransactions } from "api/transactions";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { DragDropInput } from "components/FormControls/DragDropInput";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable, Row } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { OLModal } from "components/Tooltips/OLModal";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { TRANSACTIONS_LIST_ID } from "state/tables/types";
import {
  SelectedFilter,
  SelectedFilter as SelectedFilterV2,
} from "types/filters";
import { ListingActions, ListResponse } from "types/listing";
import { backendErrors } from "utilities/backendErrors";
import { OLDate } from "utilities/dateFormat";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { formatCurrencyValue } from "utilities/numbers";
import { capitalizeFirstLetter, handleBooleanValue } from "utilities/strings";
import { MatchTransactionModal } from "../MatchTransactionModal";
import { ImportTransactionModal } from "./ImportTransactionModal";
import { useStyles } from "./TransactionList.styles";

export function TransactionList() {
  const { listing, updateListing } = useListing(TRANSACTIONS_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[TRANSACTIONS_LIST_ID]?.filters || [],
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { useTransactionsList } = useTransactions();
  const { importMatches } = useTransactions();
  const { data, error } = useTransactionsList({
    filters: parseFiltersForBE(getNewFiltersOnly(filters)),
    order: listing.order || "desc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "header:purchasedAt",
    dynamicSearch,
  }) as { data: ListResponse; error?: any };

  const setSelectedFilters = (filters: SelectedFilterV2[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      TRANSACTIONS_LIST_ID,
    );

    setFilters(filters);
  };
  const [selectedModal, setModal] = useState<{
    transaction: string;
    mode: number;
  }>();
  const [open, setOpen] = useState(false);
  const { notify } = useNotifications();
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { currency } = useAppSelector(selectCurrentStoreData);

  const headItems = createHeadItems([
    {
      headerText: message({ id: "documentNumber" }),
      property: "header:documentNumber",
      isSortable: true,
    },
    {
      headerText: message({ id: "documentType" }),
      property: "header:documentType",
      isSortable: true,
    },
    {
      headerText: message({ id: "documentDate" }),
      property: "header:purchasedAt",
      isSortable: true,
    },
    {
      headerText: message({ id: "fullName" }),
      property: "customerData:name",
      isSortable: true,
    },
    {
      headerText: message({ id: "email" }),
      property: "customerData:email",
      hasMinWidthThreshold: true,
      isSortable: true,
    },
    {
      headerText: message({ id: "phoneNumber" }),
      property: "customerData:phone",
      isSortable: true,
    },
    {
      headerText: message({ id: "loyaltyCardNumber" }),
      property: "customerData:loyaltyCardNumber",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "memberID" }),
      property: "customerData:customerId",
      isSortable: true,
      isVisible: false,
    },
    {
      headerText: message({ id: "matched" }),
      property: "matched",
      isSortable: true,
    },
    {
      align: "right",
      headerText: message({ id: "transactionValueWithCurrency" }, { currency }),
      property: "grossValue",
      isSortable: true,
    },
  ]);
  function createItems(items: any[]): Row[] {
    return items.map((item) => ({
      cells: [
        {
          content: item.header?.documentNumber,
          property: "header:documentNumber",
        },
        {
          content:
            item.header?.documentType === "sell"
              ? message({ id: "purchase" })
              : capitalizeFirstLetter(item.header?.documentType),
          property: "header:documentType",
        },
        {
          content: OLDate(item.header?.purchasedAt),
          property: "header:purchasedAt",
        },
        {
          content: item.customerData?.name,
          property: "customerData:name",
          dataHjSuppress: true,
        },
        {
          content: item.customerData?.email,
          property: "customerData:email",
          dataHjSuppress: true,
        },
        {
          content: item.customerData?.phone,
          property: "customerData:phone",
          dataHjSuppress: true,
        },
        {
          content: item.customerData?.loyaltyCardNumber,
          property: "customerData:loyaltyCardNumber",
          dataHjSuppress: true,
        },
        {
          content: item.customerId,
          align: "right",
          property: "customerData:customerId",
          dataHjSuppress: true,
        },
        {
          content: message({ id: handleBooleanValue(item.matched) }),
          property: "matched",
        },
        {
          align: "right",
          content: formatCurrencyValue(item.grossValue),
          property: "grossValue",
        },
      ],
      id: item.transactionId,
      metadata: { customerId: item.customerId },
      testId: "transaction-table-row",
    }));
  }
  const items = data?.items ? createItems(data.items) : [];

  enum Modals {
    ImportTransactions,
    MatchTransaction,
    ImportMatches,
  }
  const { useMembersList } = useMember();
  const [searchedMemberValue, setSearchedMemberValue] = useState("");
  const { data: searchedMembers } = useMembersList({
    filters: searchedMemberValue
      ? [
          {
            condition: "",
            query: "identifier",
            value: searchedMemberValue,
          },
        ]
      : [],
  }) as {
    data: ListResponse;
  };
  const { assignTransaction } = useTransactions();

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && data) {
      setPersistentFilters(filters, TRANSACTIONS_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, error, data]);

  const hasTransactionModifyPermission =
    hasModifyAccessToResource("TRANSACTION");
  const hasAnyChannelPermission = hasAnyAccessToResource("CHANNEL");

  return (
    <>
      <TopHeader title={message({ id: "transactions" })} />
      <Subheader>
        <FiltersV2
          donutLabel={message({ id: "transactions" })}
          properties={[
            ...(hasAnyChannelPermission
              ? [
                  {
                    query: "channelId",
                    label: message({ id: "channelId" }),
                    type: "channel-filter",
                  } as SelectedFilterV2,
                ]
              : []),
            {
              query: "header:labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "header:purchasedAt",
              label: message({ id: "documentDate" }),
              type: "datetime",
            },
            {
              query: "header:documentNumber",
              label: message({ id: "documentNumber" }),
              type: "transaction-document-number",
            },
            {
              query: "header:documentType",
              label: message({ id: "documentType" }),
              type: "transaction-document-type",
            },
            {
              query: "customerData:email",
              label: message({ id: "email" }),
              type: "member-email-filter",
            },
            {
              query: "customerData:name",
              label: message({ id: "fullName" }),
              type: "text",
            },
            {
              query: "grossValue",
              label: message({ id: "grossValue" }),
              type: "number",
            },
            {
              query: "customerData:loyaltyCardNumber",
              label: message({ id: "loyaltyCardNumber" }),
              type: "member-loyalty-card-number",
            },
            {
              query: "matched",
              label: message({ id: "matched" }),
              type: "boolean-yes-no",
            },
            {
              query: "customerId",
              label: message({ id: "memberID" }),
              type: "text",
            },
            {
              query: "customerData:phone",
              label: message({ id: "phoneNumber" }),
              type: "text",
            },
            {
              query: "items:labels",
              label: message({ id: "productCustomAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "items:maker",
              label: message({ id: "productBrands" }),
              type: "multiinput",
            },
            {
              query: "items:category",
              label: message({ id: "productCategories" }),
              type: "multiinput",
            },
            {
              query: "items:sku",
              label: message({ id: "productSKUs" }),
              type: "multiinput",
            },
            {
              query: "transactionId",
              label: message({ id: "transactionId" }),
              type: "text",
            },
            {
              query: "header:purchasePlace",
              label: message({ id: "transactionPurchasePlace" }),
              type: "text",
            },
          ]}
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          total={data?.total}
          data-test-id="transaction-list"
        />
        {hasTransactionModifyPermission ? (
          <SubheaderMenuButton
            text={message({ id: "addTransaction" })}
            menuItems={[
              {
                text: message({
                  id: "addTransactionManually",
                }),
                onClick: () => navigate(links.addTransaction),
              },
              {
                text: message({ id: "importMatches" }),
                onClick: () => {
                  setModal({ transaction: "", mode: Modals.ImportMatches });
                  setOpen(true);
                },
              },
              {
                text: message({ id: "importTransactions" }),
                onClick: () => {
                  setModal({
                    transaction: "",
                    mode: Modals.ImportTransactions,
                  });
                  setOpen(true);
                },
              },
              {
                text: message({ id: "matchTransaction" }),
                onClick: () => {
                  setModal({ transaction: "", mode: Modals.MatchTransaction });
                  setOpen(true);
                },
              },
            ]}
            data-test-id="transaction-list"
          />
        ) : null}
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="header:documentNumber"
            properties={[
              {
                id: "header:documentNumber",
                label: "documentNumber",
              },
              { id: "customerData:email", label: "email" },
              { id: "customerData:name", label: "name" },
              { id: "customerData:phone", label: "phone" },
            ]}
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: TRANSACTIONS_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        data-test-id="transaction-list"
        fullView
        setColumnOrdering={setColumnOrdering}
        tableName={TRANSACTIONS_LIST_ID}
        onRowClick={(id: string) => {
          navigate(links.transaction(id as string));
        }}
        sort={{
          order: listing.order || "desc",
          property: listing.property || "header:purchasedAt",
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
        }}
        actions={{
          body: [
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                navigate(links.editTransaction(id as string));
              },
              hideCondition: (item: Row) =>
                item.metadata?.customerId ||
                hasTransactionModifyPermission === false,
              label: message({
                id: "editTransaction",
              }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => {
                setModal({
                  transaction: data?.items.find(
                    (item: any) => item.transactionId === id,
                  )?.header?.documentNumber,
                  mode: Modals.MatchTransaction,
                });
                setOpen(true);
              },
              hideCondition: (item: Row) =>
                item.metadata?.customerId ||
                hasTransactionModifyPermission === false,
              label: message({ id: "matchWithMember" }),
            },
            {
              action: (
                id?: string | React.MouseEvent<HTMLLIElement, MouseEvent>,
              ) => navigate(links.transaction(id as string)),
              label: message({ id: "seeDetails" }),
            },
          ],
        }}
        columns={data && headItems}
        rows={data && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        pagination={{
          count: calculateCount(data?.total.filtered, data?.total.all),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
        clearFilters={() => setFilters([])}
      />
      {selectedModal?.mode === Modals.ImportTransactions ? (
        <ImportTransactionModal
          isModalOpen={open}
          closeModal={() => setOpen(false)}
        />
      ) : selectedModal?.mode === Modals.MatchTransaction ? (
        <MatchTransactionModal
          open={open}
          close={() => setOpen(false)}
          selectedModal={selectedModal}
          assignTransaction={assignTransaction}
          setSearchedMemberValue={setSearchedMemberValue}
          searchedMembers={searchedMembers}
        />
      ) : selectedModal?.mode === Modals.ImportMatches ? (
        <OLModal
          header={message({ id: "importMatches" })}
          onClose={() => setOpen(false)}
          open={open}
        >
          <Formik
            initialStatus={{}}
            initialValues={{ matches: undefined }}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(true);
              setStatus({});
              try {
                const response = await importMatches(values.matches?.[0]);
                if (response) {
                  setOpen(false);
                  response.data?.totalFailed === 0 &&
                  response.data?.totalSuccess > 0
                    ? notify(
                        message({
                          id: "transactionsImported",
                        }),
                      )
                    : notify(
                        message(
                          { id: "importMatchesWithErrors" },
                          {
                            processed: response.data.totalProcessed,
                            success: response.data.totalSuccess,
                            failed: response.data.totalFailed,
                          },
                        ),
                      );
                }
              } catch (exception) {
                setSubmitting(false);
                notify(getErrorMessage(exception));
                const errors = backendErrors(getBackendErrors(exception));
                setStatus(errors);
                focusError();
              }
            }}
          >
            {({
              handleSubmit,
              isSubmitting,
              setFieldValue,
              status,
              values,
            }) => {
              return (
                <Form onSubmit={handleSubmit}>
                  {(status?.[""] || status?.["file"]) && (
                    <span className={classes.error}>
                      {status[""] || status["file"]}
                    </span>
                  )}
                  <DragDropInput
                    data-test-id="transactions-import-matches-import-drag-drop"
                    className={classes.dragDropWrapper}
                    description={
                      <span className={classes.dragDropText}>
                        {message({ id: "matchesInfo" })}
                      </span>
                    }
                    header={message({
                      id: "uploadFileFromComputer",
                    })}
                    inputClassName={classes.fullWidth}
                    singleFile
                    onChange={(acceptedFiles, rejectedFiles) => {
                      setFieldValue(
                        "matches",
                        acceptedFiles.length > 0
                          ? [acceptedFiles[0]]
                          : [rejectedFiles[0].file],
                      );
                    }}
                    onDelete={() => setFieldValue("matches", undefined)}
                    value={values.matches}
                  />
                  <OLButton
                    className={classes.modalButton}
                    color="primary"
                    disabled={isSubmitting || !values.matches}
                    fullWidth
                    type="submit"
                    variant="contained"
                    data-test-id="transactions-import-matches-import-button"
                  >
                    {message({
                      id: "importMatches",
                    })}
                  </OLButton>
                </Form>
              );
            }}
          </Formik>
        </OLModal>
      ) : null}
    </>
  );
}
